import { useState, useCallback, useEffect } from 'react'
import { usePrivateChatCache } from './usePrivateChatCache'
import { apiService } from '@/services/api'
import type { 
  PrivateMessage, 
  PrivateMessageCreate, 
  PrivateConversationSummary,
  PrivateMessageEvent,
  WebSocketMessage 
} from '@/types/message'

interface UsePrivateChatProps {
  tableId: string
  currentUserId?: string
  subscribe: (fn: (data: any) => void) => () => void
  onNewMessage?: (senderId: string) => void // Callback quando mensagem chega (para atualizar contadores externos)
}

export function usePrivateChat({
  tableId,
  currentUserId,
  subscribe,
  onNewMessage
}: UsePrivateChatProps) {
  const [privateMessages, setPrivateMessages] = useState<PrivateMessage[]>([])
  const [conversations, setConversations] = useState<PrivateConversationSummary[]>([])
  const [activeConversationUserId, setActiveConversationUserId] = useState<string | null>(null)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [loadingConversations, setLoadingConversations] = useState(false)

  // Hook de cache
  const {
    getCachedMessages,
    setCachedMessages,
    addMessageToCache
  } = usePrivateChatCache({
    tableId,
    enabled: true,
    maxCacheAge: 10 * 60 * 1000 // 10 minutos
  })

  // Carregar lista de conversas
  const loadConversations = useCallback(async () => {
    if (!tableId || !currentUserId) return

    setLoadingConversations(true)
    try {
      const data = await apiService.request<PrivateConversationSummary[]>(
        `/tables/${tableId}/private-conversations`
      )
      setConversations(data)
    } catch (error) {
      console.error('Erro ao carregar conversas:', error)
    } finally {
      setLoadingConversations(false)
    }
  }, [tableId, currentUserId])

  // Carregar histórico de mensagens com usuário específico
  const loadPrivateHistory = useCallback(async (userId: string, forceRefresh: boolean = false) => {
    if (!tableId || !currentUserId || !userId) return

    // Verificar se há mensagens não lidas para esse usuário
    const conversation = conversations.find(c => c.user_id === userId)
    const hasUnreadMessages = conversation && conversation.unread_count > 0

    // Tentar carregar do cache primeiro (somente se não houver mensagens não lidas)
    if (!forceRefresh && !hasUnreadMessages) {
      const cachedMessages = getCachedMessages(userId)
      if (cachedMessages && cachedMessages.length > 0) {
        setPrivateMessages(cachedMessages)
        setActiveConversationUserId(userId)
        return
      }
    }

    setLoadingHistory(true)
    try {
      const data = await apiService.request<PrivateMessage[]>(
        `/tables/${tableId}/private-messages/${userId}`
      )

      // Salvar no cache
      setCachedMessages(userId, data)

      setPrivateMessages(data)
      setActiveConversationUserId(userId)

      // Marcar conversa como sem mensagens não lidas
      setConversations(prev =>
        prev.map(conv =>
          conv.user_id === userId
            ? { ...conv, unread_count: 0 }
            : conv
        )
      )
    } catch (error) {
      console.error('Erro ao carregar histórico:', error)
    } finally {
      setLoadingHistory(false)
    }
  }, [tableId, currentUserId, conversations, getCachedMessages, setCachedMessages])

  // Enviar mensagem privada
  const sendPrivateMessage = useCallback(async (
    receiverId: string, 
    content: string, 
    messageType: 'text' | 'action' | 'system' = 'text'
  ) => {
    if (!tableId || !currentUserId || !content.trim()) return false

    try {
      // Enviar via API REST (apiService.request lanca em non-ok → cai no catch)
      const newMessage = await apiService.request<PrivateMessage>('/private-messages', {
        method: 'POST',
        body: JSON.stringify({
          table_id: tableId,
          receiver_id: receiverId,
          content,
          message_type: messageType
        } as PrivateMessageCreate)
      })

      // Adicionar à lista local se é a conversa ativa
      if (activeConversationUserId === receiverId) {
        const messageWithOwnership = {
          ...newMessage,
          is_own_message: true
        }
        // Verificar se a mensagem já existe para evitar duplicação
        setPrivateMessages(prev => {
          const messageExists = prev.some(msg => msg.id === newMessage.id)
          if (messageExists) return prev
          return [...prev, messageWithOwnership]
        })

        // Adicionar ao cache
        addMessageToCache(receiverId, messageWithOwnership)
      }

      // Atualizar última mensagem na lista de conversas
      setConversations(prev =>
        prev.map(conv =>
          conv.user_id === receiverId
            ? {
                ...conv,
                last_message: content,
                last_message_timestamp: new Date().toISOString()
              }
            : conv
        )
      )

      // NOTA: Não enviamos via WebSocket aqui porque:
      // 1. A mensagem já foi persistida via REST API
      // 2. O backend já faz o broadcast via WebSocket quando necessário
      // 3. Enviar novamente causaria duplicação

      return true
    } catch (error) {
      console.error('Erro ao enviar mensagem privada:', error)
      return false
    }
  }, [tableId, currentUserId, activeConversationUserId, addMessageToCache])

  // Enviar mensagem privada COMO NPC (mestre enviando em nome do NPC)
  const sendPrivateMessageAsNpc = useCallback(async (
    npcUserId: string,
    receiverId: string, 
    content: string, 
    messageType: 'text' | 'action' | 'system' = 'text'
  ) => {
    if (!tableId || !currentUserId || !content.trim()) return false

    try {
      // Enviar via API REST para endpoint específico de NPC
      const newMessage = await apiService.request<PrivateMessage>(
        `/private-messages/as-npc?npc_user_id=${encodeURIComponent(npcUserId)}`,
        {
          method: 'POST',
          body: JSON.stringify({
            table_id: tableId,
            receiver_id: receiverId,
            content,
            message_type: messageType
          } as PrivateMessageCreate)
        }
      )

      // Adicionar à lista local - mensagem enviada pelo NPC
      // A conversa ativa pode ser com o receiver (perspectiva do NPC)
      if (activeConversationUserId === receiverId) {
        const messageWithOwnership = {
          ...newMessage,
          is_own_message: true  // Do ponto de vista do NPC
        }
        setPrivateMessages(prev => [...prev, messageWithOwnership])
        addMessageToCache(receiverId, messageWithOwnership)
      }

      return true
    } catch (error) {
      console.error('Erro ao enviar mensagem como NPC:', error)
      return false
    }
  }, [tableId, currentUserId, activeConversationUserId, addMessageToCache])

  // Carregar histórico como NPC (mestre vendo conversas do NPC)
  const loadPrivateHistoryAsNpc = useCallback(async (npcUserId: string, otherUserId: string) => {
    if (!tableId || !currentUserId) return

    setLoadingHistory(true)
    try {
      const data = await apiService.request<PrivateMessage[]>(
        `/tables/${tableId}/private-messages/as-npc/${npcUserId}/with/${otherUserId}`
      )
      setPrivateMessages(data)
      setActiveConversationUserId(otherUserId)
    } catch (error) {
      console.error('Erro ao carregar histórico do NPC:', error)
    } finally {
      setLoadingHistory(false)
    }
  }, [tableId, currentUserId])

  // Carregar conversas do NPC (mestre vendo lista de conversas do NPC)
  const loadNpcConversations = useCallback(async (npcUserId: string) => {
    if (!tableId || !currentUserId) return

    setLoadingConversations(true)
    try {
      const data = await apiService.request<PrivateConversationSummary[]>(
        `/tables/${tableId}/private-conversations/as-npc/${npcUserId}`
      )
      setConversations(data)
    } catch (error) {
      console.error('Erro ao carregar conversas do NPC:', error)
    } finally {
      setLoadingConversations(false)
    }
  }, [tableId, currentUserId])

  // Marcar mensagem como lida
  const markAsRead = useCallback(async (messageId: string) => {
    try {
      await apiService.request(`/private-messages/${messageId}/read`, {
        method: 'PUT'
      })

      // Atualizar localmente
      setPrivateMessages(prev =>
        prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_read: true }
            : msg
        )
      )
    } catch (error) {
      console.error('Erro ao marcar como lida:', error)
    }
  }, [])

  // Marcar TODAS as mensagens de um usuário como lidas no backend
  const markAllMessagesAsRead = useCallback(async (senderId: string) => {
    if (!tableId) return
    
    try {
      await apiService.request(`/tables/${tableId}/private/${senderId}/mark-all-read`, {
        method: 'PUT'
      })

      // Atualizar contador de não lidas na lista de conversas
      setConversations(prev => 
        prev.map(conv => 
          conv.user_id === senderId 
            ? { ...conv, unread_count: 0 }
            : conv
        )
      )
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error)
    }
  }, [tableId])

  // Conectar/desconectar de uma conversa.
  // Async: resolve quando histórico carregou E mensagens foram marcadas
  // como lidas no backend. Permite ao caller fazer refetch de contadores
  // sem precisar de setTimeout mágico.
  const connectPrivateRoom = useCallback(async (userId: string) => {
    setActiveConversationUserId(userId)
    await Promise.all([
      loadPrivateHistory(userId),
      markAllMessagesAsRead(userId),
    ])
  }, [loadPrivateHistory, markAllMessagesAsRead])

  const disconnectPrivateRoom = useCallback(() => {
    setActiveConversationUserId(null)
    setPrivateMessages([])
  }, [])

  // Processar eventos WebSocket via hub (parse único no useGameWebSocket)
  useEffect(() => {
    const handlePrivateEvent = (data: PrivateMessageEvent) => {
      try {
        switch (data.type) {
          case 'private_message':
            // Mensagem privada recebida
            if (data.sender_id && data.sender_id !== currentUserId) {
              const newMessage: PrivateMessage = {
                id: data.id!,
                table_id: data.table_id,
                sender_id: data.sender_id,
                receiver_id: data.receiver_id!,
                content: data.content!,
                message_type: data.message_type!,
                is_read: false,
                timestamp: data.timestamp!,
                created_at: data.timestamp!,
                sender_full_name: data.sender_full_name,
                is_own_message: false
              }

              // Adicionar à conversa ativa se for dessa conversa
              if (activeConversationUserId === data.sender_id) {
                // Verificar se a mensagem já existe para evitar duplicação
                setPrivateMessages(prev => {
                  const messageExists = prev.some(msg => msg.id === newMessage.id)
                  if (messageExists) return prev
                  return [...prev, newMessage]
                })
                // Adicionar ao cache
                addMessageToCache(data.sender_id, newMessage)
                // Marcar como lida automaticamente se a conversa está ativa
                setTimeout(() => markAsRead(newMessage.id), 500)
              } else {
                // Adicionar ao cache mesmo se não é a conversa ativa
                addMessageToCache(data.sender_id, newMessage)
              }

              // Atualizar contador de não lidas na lista de conversas
              setConversations(prev => 
                prev.map(conv => 
                  conv.user_id === data.sender_id
                    ? { 
                        ...conv, 
                        unread_count: activeConversationUserId === data.sender_id ? 0 : conv.unread_count + 1,
                        last_message: data.content,
                        last_message_timestamp: data.timestamp
                      }
                    : conv
                )
              )
              
              // Notificar callback externo se não é a conversa ativa
              if (activeConversationUserId !== data.sender_id && onNewMessage) {
                onNewMessage(data.sender_id)
              }
            }
            break

          case 'private_message_sent':
            // Confirmação de mensagem enviada - mensagem já foi adicionada localmente
            break

          case 'private_message_read':
            // Mensagem foi lida pelo destinatário
            if (data.message_id) {
              setPrivateMessages(prev => 
                prev.map(msg => 
                  msg.id === data.message_id 
                    ? { ...msg, is_read: true }
                    : msg
                )
              )
            }
            break
        }
      } catch (error) {
        // Ignorar mensagens que não são eventos de chat privado
      }
    }

    const unsubscribe = subscribe(handlePrivateEvent)
    return unsubscribe
  }, [subscribe, currentUserId, activeConversationUserId, markAsRead, addMessageToCache, onNewMessage])

  // Carregar conversas quando o hook é inicializado
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  return {
    // Estados
    privateMessages,
    conversations,
    activeConversationUserId,
    loadingHistory,
    loadingConversations,

    // Funções
    sendPrivateMessage,
    sendPrivateMessageAsNpc,  // Enviar como NPC
    loadPrivateHistory,
    loadPrivateHistoryAsNpc,  // Carregar histórico do NPC
    loadConversations,
    loadNpcConversations,     // Carregar conversas do NPC
    markAsRead,
    connectPrivateRoom,
    disconnectPrivateRoom
  }
}